import type { PermissionsMap } from "../auth/types";
import type { UserSchema } from "../types/auth.type";
import { inferRoleLabel } from "../auth/user-management";
import {
  flatPermissionsFromUser,
  permissionsForOrg,
} from "../auth/user-management";
import type { RoleOrgType } from "../auth/role-templates";
import { isRoleOrgType } from "../auth/role-templates";
import { OrganizationType } from "../types/organization.type";

export type { PermissionsMap };

export function normalizePermissions(raw: unknown): PermissionsMap {
  if (!raw) {
    return {};
  }

  if (Array.isArray(raw)) {
    return {};
  }

  if (typeof raw !== "object") {
    return {};
  }

  const out: PermissionsMap = {};
  for (const [orgId, roles] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(roles)) {
      const cleaned = roles.map(String).filter(Boolean);
      if (cleaned.length > 0) {
        out[String(orgId)] = cleaned;
      }
    }
  }
  return out;
}

export function isFlatPermissions(raw: unknown): raw is string[] {
  return Array.isArray(raw);
}

export function mergePermissions(a: PermissionsMap, b: PermissionsMap): PermissionsMap {
  const merged: PermissionsMap = { ...a };
  for (const [orgId, roles] of Object.entries(b)) {
    const existing = new Set((merged[orgId] || []).map(String));
    roles.forEach((r) => existing.add(String(r)));
    merged[orgId] = Array.from(existing);
  }
  return merged;
}

export function formatPermissionsForDisplay(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map(String);
  }
  if (raw && typeof raw === "object") {
    return Object.entries(raw as PermissionsMap).flatMap(([orgId, roles]) =>
      roles.map((r) => `${orgId}: ${r}`),
    );
  }
  return [];
}

export function getOrgPermissionsForDisplay(
  user: UserSchema,
  orgId: string,
): string[] {
  const perms = user.data?.permissions;
  if (Array.isArray(perms)) {
    return permissionsForOrg(perms, orgId);
  }
  const dict = normalizePermissions(perms);
  const roles = dict[orgId] ?? [];
  return roles.map((r) => `${orgId}: ${r}`);
}

export function permissionSummaryLabel(
  user: UserSchema,
  orgId: string,
  orgType: OrganizationType,
): string {
  if (!isRoleOrgType(orgType)) {
    return "Unknown";
  }
  const label = inferRoleLabel(user, orgId, orgType as RoleOrgType);
  if (label === "Custom") {
    const count = flatPermissionsFromUser(user).length
      ? permissionsForOrg(flatPermissionsFromUser(user), orgId).length
      : getOrgPermissionsForDisplay(user, orgId).length;
    return `Custom (${count} permissions)`;
  }
  return label;
}
